import { z } from 'zod';
/**
 * ehr#416 - Auto logout (ADMIN_SETTINGS_10-13-V1): after a set time with no activity in any
 * open EHR tab, a 2:00 warning appears, then the person is signed out of Credify.
 *
 * - `organizations.autoLogoutSettings`: the practice default and the custom times added to the
 *   picker. Set in Data & settings > Auto logout (admin/manager).
 * - `users.autoLogoutMinutes`: one person's own time; null follows the practice default.
 * - ehr#416 (Chase, 10-14): `clientMinutes` in the same settings: one time for every client in the
 *   client portal (no per-client times). The portal reads it from organizations.autoLogoutSettings.
 *
 * Whole minutes, 5 to 60. The sign-in itself still ends after a day (credify-login).
 */
export const AUTO_LOGOUT_MIN = 5;
export const AUTO_LOGOUT_MAX = 60;
export const AUTO_LOGOUT_DEFAULT = 15;
/** The warning shows this long before the sign-out. */
export const AUTO_LOGOUT_WARNING_SECONDS = 120;
export const AUTO_LOGOUT_BASE_PRESETS = [5, 10, 15, 20, 30, 45, 60];
export const autoLogoutMinutesSchema = z
    .number()
    .int('Enter whole minutes')
    .min(AUTO_LOGOUT_MIN, `Choose ${AUTO_LOGOUT_MIN} to ${AUTO_LOGOUT_MAX} minutes`)
    .max(AUTO_LOGOUT_MAX, `Choose ${AUTO_LOGOUT_MIN} to ${AUTO_LOGOUT_MAX} minutes`);
/** PUT /auto-logout/default */
export const autoLogoutDefaultSchema = z.object({ defaultMinutes: autoLogoutMinutesSchema });
/** PUT /auto-logout/users: null puts them back on the practice default. */
export const autoLogoutUsersSchema = z.object({
    userIds: z.array(z.string().min(1).max(100)).min(1).max(500),
    timeoutMinutes: autoLogoutMinutesSchema.nullable(),
});
/** PUT /auto-logout/client: the client portal's one time (ehr#416). */
export const autoLogoutClientSchema = z.object({ clientMinutes: autoLogoutMinutesSchema });
const isMinutes = (n) => typeof n === 'number' && Number.isInteger(n) && n >= AUTO_LOGOUT_MIN && n <= AUTO_LOGOUT_MAX;
/** Anything stored → valid settings (the default and base presets where it's missing or bad). */
export function asAutoLogoutPractice(v) {
    const o = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    const defaultMinutes = isMinutes(o.defaultMinutes) ? o.defaultMinutes : AUTO_LOGOUT_DEFAULT;
    const clientMinutes = isMinutes(o.clientMinutes) ? o.clientMinutes : AUTO_LOGOUT_DEFAULT;
    const saved = Array.isArray(o.presets) ? o.presets.filter(isMinutes) : [];
    let presets = [...AUTO_LOGOUT_BASE_PRESETS];
    for (const n of [...saved, defaultMinutes, clientMinutes])
        presets = addAutoLogoutPreset(presets, n);
    return { defaultMinutes, clientMinutes, presets };
}
/** A custom time joins the picker list, in order. */
export function addAutoLogoutPreset(presets, n) {
    return presets.includes(n) ? presets.slice() : [...presets, n].sort((a, b) => a - b);
}
/** A person's own time, else the practice default. */
export function effectiveAutoLogout(userMinutes, practice) {
    return isMinutes(userMinutes) ? userMinutes : practice;
}
/** "15 min", "1 hour". */
export const formatAutoLogout = (n) => (n === 60 ? '1 hour' : `${n} min`);
/** A user's setting as the change history shows it: "Default (15 min)" or "30 min". */
export const describeAutoLogout = (minutes, practiceDefault) => minutes == null ? `Default (${formatAutoLogout(practiceDefault)})` : formatAutoLogout(minutes);
/**
 * The idle clock: ms left before the sign-out, given the time limit and the last activity seen
 * in any tab. `warn` is true inside the last AUTO_LOGOUT_WARNING_SECONDS.
 */
export function autoLogoutRemaining(minutes, lastActivity, now) {
    const remaining = minutes * 60_000 - (now - lastActivity);
    return {
        remaining,
        warn: remaining > 0 && remaining <= AUTO_LOGOUT_WARNING_SECONDS * 1000,
        expired: remaining <= 0,
    };
}
/** "1:59" */
export function formatCountdown(ms) {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
//# sourceMappingURL=autoLogout.js.map